import { App, Plugin, VNode, DirectiveBinding, nextTick } from "vue";
import Auth from './API/Auth';
import { hasPermission } from './utils/helpers';

// Extend the Window interface
declare global {
  interface Window {
    routerData?: string; // Define router as an optional object
  }
}

interface ScannedDirective {
  componentName: string;
  directiveName: string;
  directiveValue: any | null;
  fileName?: string;
}

/**
 * Extract the controller name from routerData.
 * e.g. "User\UserController\" → "User"
 */
function getControllerFromRouterData(routerData: string | undefined): string {
  if (!routerData) return "";
  const cleaned = routerData.split("/:id").join("");
  const parts = cleaned.split("\\").filter(Boolean);
  return parts.length > 0 ? parts[0] : "";
}

/**
 * Parse a permission arg that may contain an explicit controller name.
 * Format: "ControllerName.action" → { controller: "ControllerName", action: "action" }
 * Format: "action"               → { controller: <from routerData>, action: "action" }
 */
function parsePermissionArg(arg: string | undefined, routerData: string | undefined): { controller: string; action: string } {
  const raw = arg || "";
  if (raw.includes(".")) {
    const dotIndex = raw.indexOf(".");
    const controller = raw.substring(0, dotIndex);
    const action = raw.substring(dotIndex + 1) || "";
    return { controller, action };
  }
  return { controller: getControllerFromRouterData(routerData), action: raw };
}

/**
 * Reconstruct the full directive argument from binding.arg and binding.modifiers.
 * Vue 3 treats dots as modifier separators, so v-permission:Risk.scoringFramework
 * is parsed as arg="Risk" with modifiers={scoringFramework: true}.
 * This function reconstructs it back to "Risk.scoringFramework".
 */
function getFullArg(binding: DirectiveBinding): string | undefined {
  let arg = binding.arg || "";
  if (binding.modifiers && typeof binding.modifiers === 'object') {
    const modifierKeys = Object.keys(binding.modifiers);
    if (modifierKeys.length > 0) {
      arg = arg + "." + modifierKeys.join(".");
    }
  }
  return arg || undefined;
}

/**
 * Alias for backward compatibility — delegates to the shared hasPermission helper.
 */
function hasRolePermission(controller: string, action: string): boolean {
  return hasPermission(controller, action);
}

const DirectiveScannerPlugin: Plugin = {
  install(app: App) {
    const scannedDirectives: ScannedDirective[] = [];

    app.config.globalProperties.$scannedDirectives = scannedDirectives;

    app.directive("permission", {
      unmounted(el, binding: DirectiveBinding, vnode: VNode) {
        nextTick(() => {
          const fullArg = getFullArg(binding);
          const { controller, action } = parsePermissionArg(fullArg, window.routerData);
          if (!hasRolePermission(controller, action)) {
            el.parentNode?.removeChild(el);
          }

          scannedDirectives.push({
            componentName: "UnnamedComponent",
            directiveName: `v-${fullArg || "unknown"}`,
            directiveValue: binding.value || null,
          });
        });
      },
      beforeMount(el, binding: DirectiveBinding, vnode: VNode) {
        nextTick(() => {
          const fullArg = getFullArg(binding);
          const { controller, action } = parsePermissionArg(fullArg, window.routerData);

          Auth.TABLE_PERMISSIONS = {
            "import": hasRolePermission(controller, "importFileData"),
            "export": hasRolePermission(controller, "exportAllToExcel"),
            "insert": hasRolePermission(controller, "insert"),
          };

          if (!hasRolePermission(controller, action)) {
            el.parentNode?.removeChild(el);
          }

          scannedDirectives.push({
            componentName: "UnnamedComponent",
            directiveName: `v-${fullArg || "unknown"}`,
            directiveValue: binding.value || null,
          });
        });
      },
      mounted(el, binding: DirectiveBinding, vnode: VNode) {
        nextTick(() => {
          const fullArg = getFullArg(binding);
          const { controller, action } = parsePermissionArg(fullArg, window.routerData);
          if (!hasRolePermission(controller, action)) {
            if (el.tagName === 'OPTION' && el.parentNode && el.parentNode.tagName === 'SELECT') {
              el.parentNode.removeChild(el);
            } else if (el.parentNode) {
              el.parentNode.removeChild(el);
            }
          }

          scannedDirectives.push({
            componentName: "UnnamedComponent",
            directiveName: `v-${fullArg || "unknown"}`,
            directiveValue: binding.value || null,
          });
        });
      },
    });

    app.directive("anyPermission", {
      mounted(el, binding: DirectiveBinding, vnode: VNode) {
        nextTick(() => {
          // Supports either an array of actions (e.g. ['update','insert'])
          // or an object { actions: [...], bypass: true } so public/guest
          // pages (like the online admission form) can opt out of the
          // permission gating that would otherwise strip the element.
          const rawValue = binding.value as
            | string[]
            | { actions?: string[]; bypass?: boolean };

          const isObject = rawValue && !Array.isArray(rawValue) && typeof rawValue === "object";
          const actions: string[] = isObject
            ? (rawValue as { actions?: string[] }).actions || []
            : (rawValue as string[]) || [];
          const bypass = isObject ? Boolean((rawValue as { bypass?: boolean }).bypass) : false;

          const controller = getControllerFromRouterData(window.routerData);

          const hasAny = bypass || Auth.isSuperAdmin() || actions.some((action: string) =>
            hasRolePermission(controller, action)
          );

          if (!hasAny) {
            if (el.tagName === 'OPTION' && el.parentNode && el.parentNode.tagName === 'SELECT') {
              el.parentNode.removeChild(el);
            } else if (el.parentNode) {
              el.parentNode.removeChild(el);
            }
          }
        });
      }
    });

    app.provide("$getScannedDirectives", () => scannedDirectives);
  },
};

export default DirectiveScannerPlugin;